import { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Clock, Users, Award, CheckCircle, FileText, BookOpen, Calendar, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { SEO, getCourseSchema, getBreadcrumbSchema } from "../components/SEO";
import { PromoPriceDisplay, PromoFormatPrice } from "../components/PromoPriceDisplay";
import { SelfPacedBadge } from "../components/SelfPacedBadge";
import CourseTestimonials from "../components/CourseTestimonials";
import { CohortSelector } from "../components/CohortSelector";
import { upcomingCohorts } from "../lib/cohorts";
import { PROMO_ACTIVE, PROMO_BADGE_TEXT } from "../utils/promoUtils";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedCohortId, setSelectedCohortId] = useState(searchParams.get("cohort"));
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await axios.get(`${API}/courses/${courseId}`);
        setCourse(response.data);
        const formats = response.data.delivery_formats || [];
        const wanted = searchParams.get("format") || (searchParams.get("cohort") ? "online-live" : null);
        const initial = formats.find((f) => f.format === wanted) || formats[0];
        if (initial) setSelectedFormat(initial);
      } catch (err) {
        setError("Course not found");
        console.error("Error fetching course:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const cohorts = upcomingCohorts(course, selectedFormat?.format);
  const hasCohorts = cohorts.length > 0;
  const selectedCohort = cohorts.find((c) => c.id === selectedCohortId) || null;

  // Fetch schedules when Online Live is selected
  useEffect(() => {
    if (selectedFormat?.format === "online-live") {
      fetchSchedules();
    } else {
      setSchedules([]);
      setSelectedSchedule(null);
    }
  }, [selectedFormat, courseId]);

  const fetchSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const response = await axios.get(`${API}/schedules/course/${courseId}`);
      setSchedules(response.data.schedules);
    } catch (err) {
      console.error("Error fetching schedules:", err);
      setSchedules([]);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const handleFormatChange = (formatValue) => {
    const format = course.delivery_formats.find(f => f.format === formatValue);
    setSelectedFormat(format);
    setSelectedSchedule(null);
  };

  const handleEnroll = () => {
    if (selectedFormat) {
      let url = `/checkout/${courseId}?format=${selectedFormat.format}`;
      if (hasCohorts && selectedCohort) {
        url += `&cohort=${selectedCohort.id}`;
      } else if (selectedSchedule && selectedFormat.format === "online-live") {
        url += `&schedule=${selectedSchedule.id}`;
      }
      navigate(url);
    }
  };

  const needsSelection = hasCohorts
    ? !selectedCohort
    : selectedFormat?.format === "online-live" && schedules.length > 0 && !selectedSchedule;

  const formatScheduleDate = (dateStr) => {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 w-48 skeleton rounded" />
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-64 skeleton rounded-xl" />
                <div className="h-8 w-3/4 skeleton rounded" />
                <div className="h-4 w-full skeleton rounded" />
                <div className="h-4 w-2/3 skeleton rounded" />
              </div>
              <div className="h-96 skeleton rounded-xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
          <h2 className="font-heading font-semibold text-xl mb-2">Course Not Found</h2>
          <p className="text-muted-foreground mb-4">The course you are looking for does not exist.</p>
          <Link to="/courses">
            <Button>Browse Courses</Button>
          </Link>
        </div>
      </div>
    );
  }

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Courses", url: "/courses" },
    { name: course.title, url: `/courses/${courseId}` }
  ];

  const lowestPrice = Math.min(...(course.delivery_formats?.map(f => f.price) || [0]));

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={`${course.title} | ${course.certification_level} Certification`}
        description={`${course.description?.substring(0, 155)}... PECB certified ${course.certification_level} training. Starting from $${lowestPrice}. Self-study, online live, and e-learning options.`}
        url={`/courses/${courseId}`}
        image={course.image_url}
        type="product"
        keywords={[
          course.title,
          course.category,
          course.certification_level,
          `${course.category} training`,
          `${course.certification_level} certification`,
          "PECB certified",
          "ISO training Jamaica",
          "Caribbean certification"
        ]}
        structuredData={[getCourseSchema(course), getBreadcrumbSchema(breadcrumbs)]}
      />
      {/* Header */}
      <section 
        className="relative py-16 lg:py-24"
        style={{
          backgroundImage: `url(${course.image_url || 'https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg'})`,
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >
        <div className="absolute inset-0 hero-gradient" />
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <Link to="/courses" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Courses
          </Link>
          
          <div className="max-w-3xl">
            <Badge className="bg-accent/20 text-accent border-0 mb-4">
              {course.certification_level}
            </Badge>
            <Badge className="bg-accent text-primary text-xs font-medium mb-4" data-testid="course-category">
              {course.category}
            </Badge>
            <SelfPacedBadge course={course} className="mb-4 ml-2 align-middle" />
            {PROMO_ACTIVE && (
              <Badge className="bg-red-500 text-white text-xs font-medium mb-4 ml-2">
                {PROMO_BADGE_TEXT}
              </Badge>
            )}
            <h1 className="font-heading font-bold text-3xl lg:text-5xl text-white tracking-tight mb-4" data-testid="course-title">
              {course.title}
            </h1>
            <p className="text-lg text-white/80 mb-6">
              {course.description}
            </p>
            <div className="flex flex-wrap gap-6 text-white/80">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{selectedFormat?.duration || "Variable"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                <span>PECB Certified</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <span>{course.category}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="w-full justify-start bg-white border border-border rounded-lg p-1">
                <TabsTrigger value="overview" className="flex-1 data-[state=active]:bg-brand data-[state=active]:text-white">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="curriculum" className="flex-1 data-[state=active]:bg-brand data-[state=active]:text-white">
                  Key Topics
                </TabsTrigger>
                <TabsTrigger value="outcomes" className="flex-1 data-[state=active]:bg-brand data-[state=active]:text-white">
                  Outcomes
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading">Course Overview</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h3 className="font-heading font-semibold mb-2">Description</h3>
                      <p className="text-muted-foreground">{course.description}</p>
              </div>
              
              {course.prerequisites && (
                <div>
                  <h3 className="font-heading font-semibold mb-2">Prerequisites</h3>
                  <p className="text-muted-foreground">{course.prerequisites}</p>
                </div>
              )}

              <div>
                <h3 className="font-heading font-semibold mb-3">Why Choose Us?</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {["PECB Certified Training", "Expert Instructors", "Flexible Learning Options", "Global Recognition", "Comprehensive Materials", "Post-Training Support"].map((item, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-accent" />
                            <span className="text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="curriculum" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading">Key Topics Covered</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {course.key_topics?.map((topic, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-xs font-semibold text-brand">{index + 1}</span>
                          </div>
                          <span className="text-muted-foreground">{topic}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="outcomes" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading">Learning Outcomes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      Upon successful completion of this course, you will be able to:
                    </p>
                    <ul className="space-y-3">
                      {course.learning_outcomes?.map((outcome, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Enrollment Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 border-brand/20" data-testid="enrollment-card">
              <CardHeader className="bg-brand text-white rounded-t-lg">
                <CardTitle className="font-heading">Enroll Now</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Format Selection */}
                <div>
                  <Label className="text-sm font-semibold mb-3 block">Select Course Format:</Label>
                  <RadioGroup
                    value={selectedFormat?.format}
                    onValueChange={handleFormatChange}
                    className="space-y-2"
                  >
                    {course.delivery_formats?.map((format) => (
                      <div key={format.format} className="flex items-center">
                        <RadioGroupItem
                          value={format.format}
                          id={format.format}
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor={format.format}
                          className="flex items-center justify-between w-full p-4 border rounded-lg cursor-pointer peer-data-[state=checked]:border-brand peer-data-[state=checked]:bg-brand/5 hover:border-brand/50 transition-all"
                        >
                          <div>
                            <span className="font-medium block">
                              {format.format === "self-study" && "Self-Study"}
                              {format.format === "online-live" && "Online Live"}
                              {format.format === "e-learning" && "E-Learning"}
                            </span>
                            <span className="text-xs text-muted-foreground">{format.duration}</span>
                          </div>
                          <PromoFormatPrice price={format.price} />
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Cohort (scheduled dates) selection */}
                {hasCohorts && (
                  <div className="border-t border-border pt-4">
                    <CohortSelector cohorts={cohorts} value={selectedCohort?.id} onChange={setSelectedCohortId} />
                  </div>
                )}

                {/* Schedule Selection for Online Live */}
                {!hasCohorts && selectedFormat?.format === "online-live" && (
                  <div className="border-t border-border pt-4" data-testid="schedule-selection">
                    <Label className="text-sm font-semibold mb-3 block flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-brand" />
                      Select Session Date:
                    </Label>
                    {loadingSchedules ? (
                      <div className="text-sm text-muted-foreground py-4 text-center">
                        Loading available sessions...
                      </div>
                    ) : schedules.length > 0 ? (
                      <RadioGroup
                        value={selectedSchedule?.id}
                        onValueChange={(id) => setSelectedSchedule(schedules.find(s => s.id === id))}
                        className="space-y-2"
                      >
                        {schedules.map((schedule) => (
                          <div key={schedule.id} className="flex items-center">
                            <RadioGroupItem
                              value={schedule.id}
                              id={`schedule-${schedule.id}`}
                              className="peer sr-only"
                            />
                            <Label
                              htmlFor={`schedule-${schedule.id}`}
                              className="w-full p-3 border rounded-lg cursor-pointer peer-data-[state=checked]:border-brand peer-data-[state=checked]:bg-brand/5 hover:border-brand/50 transition-all"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-sm">
                                    {formatScheduleDate(schedule.start_date)}
                                  </p>
                                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                    <Clock className="h-3 w-3" />
                                    {schedule.start_time} - {schedule.end_time}
                                  </p>
                                  {schedule.instructor_name && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Instructor: {schedule.instructor_name}
                                    </p>
                                  )}
                                </div>
                                <Badge variant="outline" className={
                                  schedule.max_participants - schedule.enrolled_count <= 5 
                                    ? "text-orange-600 border-orange-200" 
                                    : "text-green-600 border-green-200"
                                }>
                                  {schedule.max_participants - schedule.enrolled_count} spots
                                </Badge>
                              </div>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    ) : (
                      <div className="bg-slate-50 rounded-lg p-4 text-center">
                        <Calendar className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                        <p className="text-sm text-muted-foreground">
                          No scheduled sessions available.
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Contact us for custom scheduling.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Price Summary */}
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-muted-foreground">Course Price:</span>
                    <PromoPriceDisplay 
                      price={selectedFormat?.price || 0}
                      size="large"
                      showBadge={false}
                    />
                  </div>
                </div>

                {/* Enroll Button */}
                <Button 
                  className="w-full bg-brand hover:bg-brand-dark text-white text-lg py-6"
                  onClick={handleEnroll}
                  disabled={needsSelection}
                  data-testid="enroll-btn"
                >
                  {needsSelection
                    ? (hasCohorts ? "Select Your Course Dates" : "Select a Session")
                    : "Register / Enroll Now"}
                </Button>

                {/* Course Brochure */}
                {course.brochure_url && (
                  <a
                    href={course.brochure_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 text-sm text-brand hover:underline"
                  >
                    <FileText className="h-4 w-4" />
                    View Course Brochure
                  </a>
                )}

                {/* Trust Badges */}
                <div className="border-t border-border pt-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span>PECB Certified Course</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span>Secure Payment</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-accent" />
                    <span>{hasCohorts ? "Live Instructor-Led Sessions" : "Instant Access"}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <CourseTestimonials />
    </div>
  );
};

export default CourseDetail;
